var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Children, cloneElement, forwardRef } from 'react';
import { css } from '@emotion/react';
import { hideVisually } from '../../styles/style-mixins';
import styled from '../../styles/styled';
import { Button } from '../Button/Button';
import { AccessibilityError } from '../../util/errors';
const Label = styled('span')(hideVisually);
const sizeStyles = (size = 'giga') => (theme) => {
    const sizeMap = {
        kilo: theme.spacings.byte,
        giga: theme.spacings.kilo,
    };
    return css({
        padding: `calc(${sizeMap[size]} - 1px)`,
    });
};
/**
 * The IconButton component displays a button with a single icon
 * as its only child.
 */
export const IconButton = forwardRef((_a, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
ref) => {
    var { children, label, size } = _a, props = __rest(_a, ["children", "label", "size"]);
    const child = Children.only(children);
    const iconSize = size === 'kilo' ? '16' : '24';
    const icon = cloneElement(child, {
        role: 'presentation',
        size: child.props.size || iconSize,
    });
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        throw new AccessibilityError('IconButton', 'The `label` prop is missing.');
    }
    return (_jsxs(Button, Object.assign({ title: label, css: sizeStyles(size) }, props, { ref: ref }, { children: [icon, _jsx(Label, { children: label })] })));
});
IconButton.displayName = 'IconButton';
